import { NetworkAssessmentAnalysisType, NextClosestProviderAction, PatientVolumeLabelEnum, ProviderNetworkType } from "@app/projects/enumerators";
import { SummaryResultNetworkType, SummaryResultScope, SummaryResultType } from "@app/shared/data-summaries";
import { AdequacyAggregateGranularity, AdequacyAggregateType, ExportFormatType, ExportGapFilterType, ExportProviderAvailabilityType, HSDFormatType, LocationStatuses, MembershipType, NetworkProviderComparisonType, NetworkProviderFilterType, NppesStatuses, PractitionerAtLocationStatuses, PractitionerStatuses, ReportSettingsType } from "./enumerations";
import { BenchmarkingDashboardDetailsReportTypeEnum } from "@app/overviews/network-benchmarking-dashboard/dashboard-details";
import { NetworkComparisonFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/shared";
import { ZipCodeAnalysisSearchParameters, ZipClassAnalysisSearchParameters } from "@app/projects/zip-projects/interfaces";
import { ProviderSetVersionSummaryType } from "@app/provider-sets/enumerations";

interface ExportBaseModel {
  formatType: ExportFormatType;
  exportName: string;
  eMailOnSuccess: boolean;
  runAsynchronous: boolean;
  sharedUsers?: number[];
}

// Access Report
interface ExportAccessReportsUsageModel {
  clientId: number;
  endDate: Date;
  productTypeName: string;
  startDate: Date;
}

// Accuracy
interface ExportAccuracyAggregatesModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  comparedProjectRunConfigurationId?: number;
  projectURL: string;

  networkAssessmentAnalysisType: NetworkAssessmentAnalysisType;
  aggregateGranularity: AdequacyAggregateGranularity;

  includeActiveRegionSets: boolean;
  includePendingRegionSets: boolean;
  regionSetGapFilter: ExportGapFilterType;

  includePhysicianSpecialties: boolean;
  includeFacilitySpecialties: boolean;
  specialtyGapFilter: ExportGapFilterType;

  specialtyGroupCodes: string[];
  inspectionUids?: string[];
  includeInspectorResultPercentageColumns: boolean;
  includeExpandedOutcomes: boolean;

  showDifferencesOnly?: boolean;

  stateFilters?: string[];
  countyFilters?: string[];
}

interface ExportAccuracySummariesModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  projectURL: string;

  includeActiveRegionSets: boolean;
  includePendingRegionSets: boolean;
  regionSetGapFilter: ExportGapFilterType;

  includePhysicianSpecialties: boolean;
  includeFacilitySpecialties: boolean;
  specialtyGapFilter: ExportGapFilterType;

  regionSetIds: number[];
  specialtyGroupCodes: string[];
}

interface ExportAccuracyActionableProvidersModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  comparedProjectRunConfigurationId?: number;
  projectURL: string;

  networkAssessmentAnalysisType: NetworkAssessmentAnalysisType;
  providerNetworkType: ProviderNetworkType;

  includeActiveRegionSets: boolean;
  includePendingRegionSets: boolean;

  includePractitionerEntityType: boolean;
  includeServiceLocationEntityType: boolean;

  specialtyGroupCodes: string[];

  providerIds: number[];

  inspectionBand: string;
  inspectionUids: string[];

  recommendedActions: number[];
  recommendedActionReasons: string[];
  showAllRecommendedActionReasons: boolean;
  filterByTrueCounty: boolean;
  rowTypes: number[];
  practitionerAtLocationConfidenceScores: number[];
  pcaLabels: number[];

  stateFilters?: string[];
  countyFilters?: string[];
}

interface ExportStatusReportModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  projectURL: string;

  networkAssessmentAnalysisType: NetworkAssessmentAnalysisType;
  providerNetworkType: ProviderNetworkType;

  includeActiveRegionSets: boolean;
  includePendingRegionSets: boolean;

  inspectionBand: string;
  inspectionUids: string[];

  specialtyGroupCodes: string[];

  practitionerStatuses: PractitionerStatuses[];
  locationStatuses: LocationStatuses[];
  practitionerAtLocationStatuses: PractitionerAtLocationStatuses[];
  nppesStatuses: NppesStatuses[];

  providerIds: number[];
  stateFilters?: string[];
  countyFilters?: string[];
}

interface ExportRecommendedActionReasonModel {
  reason: string;
  friendlyName: string;
}

// Adequacy
interface ExportAdequacyAggregatesModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  comparedProjectRunConfigurationId?: number;
  projectURL: string;

  networkAssessmentAnalysisTypes: NetworkAssessmentAnalysisType[];
  aggregateType: AdequacyAggregateType;
  aggregateGranularity: AdequacyAggregateGranularity;
  regionSetGapFilter: ExportGapFilterType;
  specialtyGapFilter: ExportGapFilterType;

  includeActiveRegionSets: boolean;
  includeApprovedRegionSets: boolean;
  includePendingRegionSets: boolean;
  includePhysicianSpecialties: boolean;
  includeFacilitySpecialties: boolean;

  regionSetIds: number[];
  specialtyGroupCodes: string[];
  reportCustomizationId?: number;
}

interface ExportAdequacyVolumeAggregateRequestModel extends ExportBaseModel {
  customProjectIds: number[];
  projectRunConfigurationId?: number;
  comparedProjectRunConfigurationId?: number;
  allowParallelization?: boolean;
  generateNoDataFoundReadme: boolean;
  publishedOnly: boolean;
  separateReportOutput?: boolean;
  volumeReportGroupMemorySize?: number;
  isVolumeReport: boolean;
  aggregateType: AdequacyAggregateType;
  aggregateGranularity: AdequacyAggregateGranularity;
  networkAssessmentAnalysisTypes: NetworkAssessmentAnalysisType[];
  includeActiveRegionSets: boolean;
  includeApprovedRegionSets: boolean;
  includePendingRegionSets: boolean;
  includeFacilitySpecialties: boolean;
  includePhysicianSpecialties: boolean;
  regionSetGapFilter: ExportGapFilterType;
  specialtyGapFilter: ExportGapFilterType;
  regionSetIds: number[];
  specialtyGroupCodes: string[];
  reportCustomizationId?: number;
}

interface ExportAdequacySummariesModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  comparedProjectRunConfigurationId?: number;
  projectURL: string;

  networkAssessmentAnalysisType: NetworkAssessmentAnalysisType;

  includeActiveRegionSets: boolean;
  includeApprovedRegionSets: boolean;
  includePendingRegionSets: boolean;
  regionSetGapFilter: ExportGapFilterType;

  includeExpandableSpecialties: boolean;
  includePhysicianSpecialties: boolean;
  includeFacilitySpecialties: boolean;
  specialtyGapFilter: ExportGapFilterType;

  regionSetIds: number[];
  showCMSCredit: boolean;
  specialtyGroupCodes: string[];
  changedGapsOnly: boolean;

  reportCustomizationId?: number;
}

interface ExportAdequacySpecialtySummaryProjectModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  comparedProjectRunConfigurationId?: number;
  projectURL: string;
  networkAssessmentAnalysisTypes: NetworkAssessmentAnalysisType[];
  includeActiveRegionSets: boolean;
  includeApprovedRegionSets: boolean;
  includePendingRegionSets: boolean;
  regionSetGapFilter: ExportGapFilterType;

  includePhysicianSpecialties: boolean;
  includeFacilitySpecialties: boolean;
  specialtyGapFilter: ExportGapFilterType;

  regionSetIds: number[];
  showExpandableSpecialties: boolean;
  specialtyGroupCodes: string[];
  changedGapsOnly: boolean;
  membershipType: MembershipType;

  reportCustomizationId?: number;
}

interface ExportAdequacyServicingProvidersModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  comparedProjectRunConfigurationId?: number;
  nextClosestProviderAction?: NextClosestProviderAction;
  projectURL: string;

  providerNetworkType: ProviderNetworkType;
  networkAssessmentAnalysisTypes: NetworkAssessmentAnalysisType[];
  impactProviderType: ExportProviderAvailabilityType;
  providerComparisonTypes: NetworkProviderComparisonType[];

  includeActiveRegionSets: boolean;
  includeApprovedRegionSets: boolean;
  includePendingRegionSets: boolean;
  regionSetGapFilter: ExportGapFilterType;

  includePhysicianSpecialties: boolean;
  includeFacilitySpecialties: boolean;
  specialtyGapFilter: ExportGapFilterType;

  networkProviderFilterType: NetworkProviderFilterType;

  regionSetIds: number[];

  specialtyGroupCodes: string[];

  providerIds: number[];

  hsdFormat: HSDFormatType;
  appendPatternOfCareProviders: boolean;

  providerClaimsVolumeFilter: PatientVolumeLabelEnum;

  isZipAnalysis: boolean;
  reportCustomizationId?: number;
  includeCargoColumns?: boolean;
}

// based on ExportZipSummariesRequestModel on the API
// leaving out the properties that aren't used
interface ZipSummariesReportModel extends ExportBaseModel {
  changedGapsOnly: boolean;
  comparedProjectRunConfigurationId?: number;
  customProjectId: number;
  includeActiveRegionSets: boolean;
  includeApprovedRegionSets: boolean;
  includeFacilitySpecialties: boolean;
  includePendingRegionSets: boolean;
  includePhysicianSpecialties: boolean;
  networkAssessmentAnalysisTypes: NetworkAssessmentAnalysisType[];
  projectRunConfigurationId: number;
  projectUrl: string;
  regionSetIds: number[];
  reportCustomizationId?: number;
  specialtyGapFilter: string;
  specialtyGroupCodes: string[];
}

interface ClosestProviderReportModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  projectUrl: string;
  regionSetIds: number[];
  reportCustomizationId?: number;
  networkAssessmentAnalysisType: NetworkAssessmentAnalysisType;
}

// Attestation
interface ExportAttestationAggregatesModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  projectURL: string;

  includeActiveRegionSets: boolean;
  includeApprovedRegionSets: boolean;
  includePendingRegionSets: boolean;
  regionSetGapFilter: ExportGapFilterType;

  includePhysicianSpecialties: boolean;
  includeFacilitySpecialties: boolean;
  specialtyGapFilter: ExportGapFilterType;

  regionSetIds: number[];
  specialtyGroupCodes: string[];
}

interface ExportAttestationSummariesModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  projectURL: string;

  includeActiveRegionSets: boolean;
  includeApprovedRegionSets: boolean;
  includePendingRegionSets: boolean;
  regionSetGapFilter: ExportGapFilterType;

  includePhysicianSpecialties: boolean;
  includeFacilitySpecialties: boolean;
  specialtyGapFilter: ExportGapFilterType;

  regionSetIds: number[];
  specialtyGroupCodes: string[];
}

interface ExportAttestationServicingProvidersModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  comparedProjectRunConfigurationId?: number;
  projectURL: string;

  providerNetworkType: ProviderNetworkType;

  includeActiveRegionSets: boolean;
  includeApprovedRegionSets: boolean;
  includePendingRegionSets: boolean;
  regionSetGapFilter: ExportGapFilterType;

  includePhysicianSpecialties: boolean;
  includeFacilitySpecialties: boolean;
  specialtyGapFilter: ExportGapFilterType;

  hsdFormat: HSDFormatType;

  regionSetIds: number[];

  specialtyGroupCodes: string[];

  providerIds: number[];
}

// Composite
interface ExportCompositeAggregatesModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  projectURL: string;

  includeActiveRegionSets: boolean;
  includeApprovedRegionSets: boolean;
  includePendingRegionSets: boolean;
  regionSetGapFilter: ExportGapFilterType;

  includePhysicianSpecialties: boolean;
  includeFacilitySpecialties: boolean;
  specialtyGapFilter: ExportGapFilterType;

  regionSetIds: number[];
  specialtyGroupCodes: string[];
}

interface ExportCompositeSummariesModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  projectURL: string;

  includeActiveRegionSets: boolean;
  includeApprovedRegionSets: boolean;
  includePendingRegionSets: boolean;
  regionSetGapFilter: ExportGapFilterType;

  includePhysicianSpecialties: boolean;
  includeFacilitySpecialties: boolean;
  specialtyGapFilter: ExportGapFilterType;

  regionSetIds: number[];
  specialtyGroupCodes: string[];
}

interface ExportCompositeServicingProvidersModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  comparedProjectRunConfigurationId?: number;
  projectURL: string;

  providerNetworkType: ProviderNetworkType;

  includeActiveRegionSets: boolean;
  includeApprovedRegionSets: boolean;
  includePendingRegionSets: boolean;
  regionSetGapFilter: ExportGapFilterType;

  includePhysicianSpecialties: boolean;
  includeFacilitySpecialties: boolean;
  specialtyGapFilter: ExportGapFilterType;

  hsdFormat: HSDFormatType;

  regionSetIds: number[];

  specialtyGroupCodes: string[];

  providerIds: number[];
}

// Zip Analysis
interface ExportZipAnalysisRequestModel extends ExportBaseModel {
  includeExpandableSpecialties: boolean;
  includeFacilitySpecialties: boolean;
  includePhysicianSpecialties: boolean;
  regionSetIds: number[];
  specialtyGapFilter: ExportGapFilterType;
  regionSetGapFilter: ExportGapFilterType;
  specialtyGroupCodes: string[];
  networkAssessmentAnalysisType: NetworkAssessmentAnalysisType;
  searchParametersModel: ZipCodeAnalysisSearchParameters | ZipClassAnalysisSearchParameters;
  comparedProjectRunConfigurationId?: number;
}

// User Login Statistics
interface ExportUserLoginStatisticsRequestModel extends ExportBaseModel {
  clientId: number;
  includeGuestLogins: boolean;
}

// Summaries
interface ExportSummariesDetailProvidersRequestModel extends ExportBaseModel {
  customProjectId: number;
  projectRunConfigurationId: number;
  projectURL: string;

  scope: SummaryResultScope;
  networkType: SummaryResultNetworkType;
  resultType: SummaryResultType;
  key: string;
  displayKey: string;
}

interface RegionSetFilter {
  networkAssessmentAnalysisType: NetworkAssessmentAnalysisType;
  secondaryNetworkAssessmentAnalysisType?: NetworkAssessmentAnalysisType;
  filterType: ExportGapFilterType;
  includeActive: boolean;
  includePending: boolean;
  includeApproved: boolean;
}

interface SpecialtyFilter {
  filterType: ExportGapFilterType;
  includeFacilitySpecialties: boolean;
  includePhysicianSpecialties: boolean;
  includeExpandableSpecialties: boolean;
}

interface EntityTypeFilter {
  includePractitionerEntityType: boolean;
  includeServiceLocationEntityType: boolean;
}

interface HSDFormatFilter {
  filterType: HSDFormatType;
  includeFacilitySpecialties: boolean;
  includePhysicianSpecialties: boolean;
}

interface ReportSettingsModel {
  id?: number;
  clientId?: number;
  userId?: number;
  type: ReportSettingsType;
  isDefault: boolean;
  name: string;
  reportSettingsJson: string;

  displayActions?: boolean;
  displayNameInput?: boolean;
  displayToDelete?: boolean;
}

interface VolumeSummariesRequestModel extends ExportBaseModel {
  customProjectIds: number[];
  networkAssessmentAnalysisType?: NetworkAssessmentAnalysisType;
  includeExceptionComment?: boolean;
  allowParallelization?: boolean;
  includeActiveRegionSets: boolean;
  includePendingRegionSets: boolean;
  includeApprovedRegionSets: boolean;
  regionSetGapFilter: ExportGapFilterType;
  regionSetIds: number[];
  specialtyGapFilter: ExportGapFilterType;
  specialtyGroupCodes: string[];
  separateReportOutput?: boolean;
  publishedOnly?: boolean;
  generateNoDataFoundReadme: boolean;
  isVolumeReport: boolean;
  sharedUsers: number[];
  reportCustomizationId?: number;
}

interface VolumeAdequacySpecialtySummariesRequestModel extends ExportBaseModel {
  customProjectIds: number[];
  networkAssessmentAnalysisTypes?: NetworkAssessmentAnalysisType[];
  allowParallelization?: boolean;
  includeActiveRegionSets: boolean;
  includePendingRegionSets: boolean;
  includeApprovedRegionSets: boolean;
  regionSetGapFilter: ExportGapFilterType;
  regionSetIds: number[];
  specialtyGapFilter: ExportGapFilterType;
  specialtyGroupCodes: string[];
  separateReportOutput?: boolean;
  publishedOnly?: boolean;
  generateNoDataFoundReadme: boolean;
  isVolumeReport: boolean;
  sharedUsers: number[];
  showExceptionComment?: boolean;
  membershipType: MembershipType;
  reportCustomizationId?: number;
}

export interface ExportCmsExceptionReportRequestModel extends ExportBaseModel {
  documentType: string;
  specialtyGroupExceptionIds: number[];
}

enum ExportHsdColumnsEnum {
  Unknown,
  AdditionalGeographic,
}

enum ExportImpactProviderTypeEnum {
  Unknown,
  Available,
  Targeted,
  Excluded,
  All,
  Changed,
}

enum ExportNextClosestProviderActionEnum {
  Unknown,
  None,
  FilterToNextClosestProviderOnly,
  AppendNextClosestProviders,
}

interface VolumeServicingProvidersRequestModel extends ExportBaseModel {
  customProjectIds: number[];
  publishedOnly: boolean;
  networkAssessmentAnalysisTypes: NetworkAssessmentAnalysisType[];
  providerNetworkTypes: ProviderNetworkType[];
  hsdFormat: boolean;
  hsdColumns: ExportHsdColumnsEnum;
  includeCargoColumns: boolean;
  cargoColumnsToInclude: string[];
  networkProviderFilterType: NetworkProviderFilterType;
  impactProviderType: ExportImpactProviderTypeEnum;
  nextClosestProviderAction: ExportNextClosestProviderActionEnum;
  specialtyGapFilter: ExportGapFilterType;
  allowParallelization: boolean;
  separateReportOutput: boolean;
  generateNoDataFoundReadme: boolean;
  IncludeActiveRegionSets: boolean;
  IncludePendingRegionSets: boolean;
  IncludeApprovedRegionSets: boolean;
  regionSetGapFilter: ExportGapFilterType;
  IncludePhysicianSpecialties: boolean;
  IncludeFacilitySpecialties: boolean;
  regionSetIds: number[];
  specialtyGroupCodes: string[];
  sharedUsers: number[];
  isCCIIOPPFReport: boolean;
  isDistinctServicingProviderReport: boolean;
  appendPatternOfCareProviders: boolean;
  suppressHSDColumns: boolean;
  reportCustomizationId?: number;
}

interface ExportCCIIOProviderModel extends ExportBaseModel {
  includeInvalidCCIIORecords: boolean;
  providerSetIds: number[];
}

interface ExportCCIIODataValidationsModel extends ExportBaseModel {
  providerSetIds: number[];
  separateReportOutput: boolean;
}

interface SharedUserModel {
  userId: number;
  userName: string;
  email: string;
  fullName: string;
}

interface VolumeZipSummariesRequestModel extends ExportBaseModel {
  customProjectIds: number[];
  allowParallelization?: boolean;
  generateNoDataFoundReadme?: boolean;
  publishedOnly: boolean;
  separateReportOutput?: boolean;
  volumeReportGroupMemorySize?: number;

  networkAssessmentAnalysisType: NetworkAssessmentAnalysisType;
  includeActiveRegionSets: boolean;
  includeApprovedRegionSets: boolean;
  includePendingRegionSets: boolean;
  includeFacilitySpecialties: boolean;
  includePhysicianSpecialties: boolean;
  regionSetGapFilter: ExportGapFilterType;
  specialtyGapFilter: ExportGapFilterType;
  regionSetIds: number[];
  specialtyGroupCodes: string[];
  sharedUsers: number[];
  reportCustomizationId?: number;
}

interface ExportPciCrossProjectRequestModel extends ExportBaseModel {
  customProjectIds: number[];
  separateReportOutput: boolean;
}

interface ExportBenchmarkingDashboardDetailsModel {
  filters: NetworkComparisonFiltersPayload;
  reportType: BenchmarkingDashboardDetailsReportTypeEnum;
  emailOnSuccess: boolean;
  sharedUsers: number[];
  exportName: string;
}

interface ExportProvidersModel {
  fileBaseName: string;
  providerFilter: string;
  providerSetId: number;
  providerSetVersionId: number;
  summaryType: ProviderSetVersionSummaryType;
}

type ExportBulkSpecialtyGroupExceptionsModel = ExportBaseModel;

export {
  ExportAccessReportsUsageModel,
  ExportAccuracyAggregatesModel,
  ExportAccuracySummariesModel,
  ExportAccuracyActionableProvidersModel,
  ExportRecommendedActionReasonModel,
  ExportAdequacyAggregatesModel,
  ExportAdequacySummariesModel,
  ExportAdequacyServicingProvidersModel,
  ExportAttestationAggregatesModel,
  ExportAttestationSummariesModel,
  ExportAttestationServicingProvidersModel,
  ExportCompositeAggregatesModel,
  ExportCompositeSummariesModel,
  ExportCompositeServicingProvidersModel,
  ExportStatusReportModel,
  ExportSummariesDetailProvidersRequestModel,
  ExportUserLoginStatisticsRequestModel,
  ExportZipAnalysisRequestModel,
  ExportAdequacyVolumeAggregateRequestModel,
  RegionSetFilter,
  ReportSettingsModel,
  SpecialtyFilter,
  EntityTypeFilter,
  HSDFormatFilter,
  ZipSummariesReportModel,
  ClosestProviderReportModel,
  VolumeServicingProvidersRequestModel,
  ExportNextClosestProviderActionEnum,
  ExportImpactProviderTypeEnum,
  VolumeSummariesRequestModel,
  VolumeZipSummariesRequestModel,
  ExportCCIIOProviderModel,
  ExportCCIIODataValidationsModel,
  SharedUserModel,
  ExportPciCrossProjectRequestModel,
  ExportBenchmarkingDashboardDetailsModel,
  ExportAdequacySpecialtySummaryProjectModel,
  VolumeAdequacySpecialtySummariesRequestModel,
  ExportBulkSpecialtyGroupExceptionsModel,
  ExportProvidersModel
};
